import React, { useState } from 'react';
import Icon from './AppIcon';
import supabase from '../../supabase-client';

const PasswordChangeModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('form'); // 'form' | 'confirm' | 'loading'
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setMessage({ type: '', text: '' });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Todos los campos son obligatorios' });
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }

    // Ir al paso de confirmación
    setStep('confirm');
  };

  const handleConfirmChange = async () => {
    setStep('loading');
    setMessage({ type: '', text: '' });

    try {
      // Actualizar contraseña usando función SQL con permisos
      const { data, error } = await supabase.rpc('update_admin_password', {
        p_new_password: formData.newPassword
      });

      if (error) throw new Error('Error al actualizar la contraseña: ' + error.message);
      
      if (data && !data.success) throw new Error(data.message || 'Error desconocido');

      setMessage({ type: 'success', text: '¡Contraseña actualizada exitosamente!' });
      
      // Cerrar modal después de 2 segundos
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      setStep('form');
    }
  };

  const handleClose = () => {
    setStep('form');
    setFormData({ newPassword: '', confirmPassword: '' });
    setMessage({ type: '', text: '' });
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const handleCancel = () => {
    if (step === 'confirm') {
      setStep('form');
    } else {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <img 
            src="/src/assets/favicon.svg" 
            alt="Logo" 
            className="w-12 h-12"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        {step === 'form' && (
          <>
            <h3 className="text-lg font-semibold text-slate-800 mb-3 text-center">
              Cambiar Contraseña
            </h3>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    <Icon name={showNewPassword ? "EyeOff" : "Eye"} size={18} />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Repite la nueva contraseña"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={18} />
                  </button>
                </div>
              </div>
              
              {message.text && (
                <div className={`p-3 rounded-md text-sm ${
                  message.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors text-center"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                >
                  Continuar
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h3 className="text-lg font-semibold text-slate-800 mb-3 text-center">
              ¿Cambiar contraseña?
            </h3>
            
            <p className="text-slate-600 mb-6 text-center">
              ¿Estás seguro que quieres cambiar tu contraseña de administrador?
              <br />
              <span className="font-medium text-slate-800">Esta acción actualizará tu contraseña inmediatamente.</span>
            </p>
            
            {message.text && (
              <div className={`p-3 rounded-md text-sm mb-4 ${
                message.type === 'success' 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {message.text}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors text-center"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmChange}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center"
              >
                Sí, Cambiar Contraseña
              </button>
            </div>
          </>
        )}

        {step === 'loading' && (
          <>
            <h3 className="text-lg font-semibold text-slate-800 mb-3 text-center">
              Cambiando Contraseña...
            </h3>
            
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={32} className="animate-spin text-blue-600" />
            </div>
            
            {message.text && (
              <div className={`p-3 rounded-md text-sm ${
                message.type === 'success' 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {message.text}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PasswordChangeModal;

